import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ToastCheckoutButton } from './CheckoutModeButtons'
import { InternationalPhoneInput } from 'src/shared/components/common/form_input/InternationalPhoneInput'
import { t, Trans } from 'src/translations/src'
import { LoginStage, PwlessAuth } from '../account/pwlessAuth/PwlessAuth'
import { AuthenticationSource } from './checkoutUtils'
import Image from 'shared/components/common/Image'
import { useModal } from 'src/shared/components/common/modal/useModal'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal'
import { useFormContext, useWatch } from 'react-hook-form'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { isValidPhoneNumber } from 'libphonenumber-js'
import {
  ToastTOSAndPrivacy,
  CAInfo
} from 'src/public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import cx from 'classnames'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { MoreVertIcon } from '@toasttab/buffet-pui-icons'
import Button from 'shared/components/common/button'
import { alertSuccess, alertError } from 'shared/js/alertUtils'
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection'
import { CompleteProfileFormInputs } from '../account/pwlessAuth/CompleteProfileForm'
import InputField from 'shared/components/common/form_input/InputField'
import { isValidCustomerEmail } from 'shared/js/emailUtils'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { dummyAddressValue } from 'public/components/online_ordering/addressUtils'
import { useCart } from 'public/components/online_ordering/CartContext'
import { usePayment } from 'src/public/components/online_ordering/PaymentContext'
import { ToastPaymentType } from 'src/public/components/online_ordering/types'
import AnimatedSection from './AnimatedSection'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { useResetDisableAccountCreation } from './useResetDisableAccountCreation'
import AskMapsDisclaimer from './AskMapsDisclaimer'

const saveValuesForCheckout = (
  formInputs: CompleteProfileFormInputs,
  setValue: (name: string, value: any) => void
) => {
  setValue('yourInfoEmail', formInputs.email)
  setValue('yourInfoFirstName', formInputs.firstName)
  setValue('yourInfoLastName', formInputs.lastName)
}

const CheckoutAuth = () => {
  const { customer, pwlessLogout } = useCustomer()
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const { setValue } = useFormContext()
  const { paymentType } = usePayment()
  const handleLogout = useCallback(async () => {
    const success = await pwlessLogout()
    if (success) {
      alertSuccess(t('user-nav.now-logged-out'))
    } else {
      alertError(t('logout.something-went-wrong'))
    }
  }, [pwlessLogout])

  const handleChangeAccount = useCallback(async () => {
    await handleLogout()
    setTimeout(() => {
      setAuthModalOpen(true)
    }, 750) // the modal opening instantly is actually kind of jarring
  }, [handleLogout])

  return (
    <div className='checkoutAuth' data-testid='auth-info-container'>
      <AskMapsDisclaimer />
      {customer ? (
        <AuthedCustomer
          onLogout={handleLogout}
          onChangeAccount={handleChangeAccount}
        />
      ) : (
        <div
          className='checkoutAuthContainer'
          data-testid='checkout-auth-container'
        >
          <ToastCheckoutButton />
          <AnimatedSection
            expanded={paymentType !== ToastPaymentType.APPLE_PAY}
          >
            <OrDivider />
            <ContactSection />
          </AnimatedSection>
        </div>
      )}
      <Modal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)}>
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.ChangeAccountButton}
          showBackButton={false}
          saveValuesForCheckout={(formInputs) =>
            saveValuesForCheckout(formInputs, setValue)
          }
        />
      </Modal>
      <hr className='separator' />
    </div>
  )
}

const AuthedCustomer = ({
  onLogout,
  onChangeAccount
}: {
  onLogout: () => Promise<void>
  onChangeAccount: () => Promise<void>
}) => {
  const { customer } = useCustomer()
  const [accountOptionsModalOpen, setAccountOptionsModalOpen] = useState(false)

  if (!customer) {
    return null
  }

  return (
    <div
      className='authedCustomerContainer'
      data-testid='authed-customer-container'
    >
      <div className='customerInfo'>
        <Image
          aria-hidden='true'
          alt='Toast logo'
          src='icons/toast-logo-filled.svg'
        />
        <div className='customerEmail'>{customer.email}</div>
      </div>
      {/*type=button so as not to submit the form*/}
      <button
        className='accountOptionsButton'
        type='button'
        onClick={() => setAccountOptionsModalOpen(true)}
      >
        <MoreVertIcon aria-label={t('checkout-auth.account-options')} />
      </button>
      <Modal
        isOpen={accountOptionsModalOpen}
        onClose={() => setAccountOptionsModalOpen(false)}
      >
        <ModalOverlay fadeIn />
        <ModalContent contentClassName='modalContentV2 bottomDrawerMobile accountOptionsModal'>
          <div className='header'>
            {t('checkout-auth.account-options')}
            <ModalCloseButton />
          </div>
          <div className='body'>
            <Button className='changeAccountButton' onClick={onChangeAccount}>
              {t('checkout-auth.change-accounts')}
            </Button>
            <Button className='signOutButton' onClick={onLogout}>
              {t('checkout-auth.sign-out')}
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  )
}

const ContactSection = () => {
  const { isOpen, onClose, onOpen } = useModal()
  const phoneNumber = useWatch({ name: 'yourInfoPhone' }) as string
  const { passwordlessLogin } = useCustomer()
  const { paymentType } = usePayment()
  const { autoPromptAuth, setAutoPromptAuth, setDisableAccountCreation } =
    useCheckout()
  const { cart } = useCart()
  const {
    formState: { errors, touchedFields },
    setValue,
    getValues
  } = useFormContext()
  const [loginStage, setLoginStage] = useState(LoginStage.EnterPhone)
  const [expandedDisclaimer, setExpandedDisclaimer] = useState(false)
  const [allowRetry, setAllowRetry] = useState(false)
  const sendPwlessLoginCode = useCallback(async () => {
    return await passwordlessLogin(phoneNumber || '')
  }, [phoneNumber, passwordlessLogin])
  const tracker = useTracker()

  // Use custom hook to reset disableAccountCreation when phone number changes
  useResetDisableAccountCreation()

  const [zipcode, setZipCode] = useState(
    cart?.order?.deliveryInfo?.zipCode === dummyAddressValue
      ? ''
      : cart?.order?.deliveryInfo?.zipCode
  )

  const required = useMemo(
    () => paymentType !== ToastPaymentType.APPLE_PAY,
    [paymentType]
  )

  const handleSendPwlessLoginCode = useCallback(async () => {
    const result = await sendPwlessLoginCode()
    if (result.success) {
      onOpen()
      setLoginStage(LoginStage.EnterVerificationCode)
      setAllowRetry(true)
    } else {
      // Silently fail - don't open modal if code sending failed
      setAllowRetry(false)
      // Disable the "Create a Toast Account" checkbox when passwordless login fails
      setDisableAccountCreation(true)
    }
  }, [onOpen, sendPwlessLoginCode, setDisableAccountCreation])

  useEffect(() => {
    if (errors.yourInfoPhone) {
      setAllowRetry(false)
    }
  }, [errors.yourInfoPhone])

  useEffect(() => {
    // Automatically send pwless login code if phone number is valid, but don't auto send
    // if we haven't touched the yourInfoPhone field -- that's a sign that the user was logged in,
    // and elected to sign out (at which point the phone input still retains their number).
    if (
      isValidPhoneNumber(phoneNumber || '') &&
      touchedFields.yourInfoPhone &&
      autoPromptAuth
    ) {
      tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
        source: AuthenticationSource.GuestCheckoutAutomatic,
        codeSentAutomatically: true,
        retry: false
      })
      setTimeout(() => {
        handleSendPwlessLoginCode()
      }, 500) // opening instantly is actually a bit jarring
    }
  }, [
    handleSendPwlessLoginCode,
    phoneNumber,
    touchedFields.yourInfoPhone,
    autoPromptAuth,
    tracker
  ])

  const handleRetry = useCallback(async () => {
    tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
      source: AuthenticationSource.GuestCheckoutAutomatic,
      codeSentAutomatically: true,
      retry: true
    })
    await handleSendPwlessLoginCode()
    setAllowRetry(true)
  }, [tracker, handleSendPwlessLoginCode])

  const validateEmail = useCallback(
    (value: string) =>
      !isValidCustomerEmail(value) && touchedFields?.yourInfoEmail && required
        ? t('customer-info-section.enter-valid-email-address')
        : true,
    [touchedFields?.yourInfoEmail, required]
  )

  // only display zipcode input for certain countries when no valid zipcode found in deliveryInfo
  const [includeZipCodeCollection, zipLabel] = useMemo(() => {
    const deliveryInfo = cart?.order?.deliveryInfo
    const collect =
      (deliveryInfo?.country === 'IE' || deliveryInfo?.country === 'GB') &&
      cart?.diningOptionBehavior === DiningOptionBehavior.Delivery &&
      (!deliveryInfo.zipCode || deliveryInfo.zipCode === dummyAddressValue)
    const label =
      deliveryInfo?.country === 'IE'
        ? t('customer-info-section.eircode')
        : t('customer-info-section.postcode')
    return [collect, label]
  }, [cart?.order?.deliveryInfo, cart?.diningOptionBehavior])

  return (
    <CheckoutSection
      title={t('checkout-auth.contact')}
      className='contactSection'
      noSectionDivider
    >
      <InputField
        id='yourInfoEmail'
        type='email'
        autoComplete='email'
        label={t('customer-info-section.email')}
        required={required}
        validate={validateEmail}
        preventFormSubmit
      />
      <div className='nameContainer'>
        <InputField
          id='yourInfoFirstName'
          type='text'
          autoComplete='given-name'
          label={t('customer-info-section.first-name')}
          required={required}
          preventFormSubmit
        />
        <InputField
          id='yourInfoLastName'
          type='text'
          autoComplete='family-name'
          label={t('customer-info-section.last-name')}
          required={required}
        />
      </div>
      <InternationalPhoneInput
        value={phoneNumber || ''}
        id='yourInfoPhone'
        required={required}
        label={t('pwless-auth.phone-number')}
        unregisterOnUnmount={false}
        onRetry={allowRetry ? handleRetry : undefined}
      />
      {includeZipCodeCollection && (
        <InputField
          id='deliveryZipCode'
          type='text'
          label={zipLabel}
          autoComplete='zipcode'
          preventFormSubmit
          value={zipcode || ''}
          required={required}
          onChange={(e) => {
            setZipCode(e.target.value)
          }}
        />
      )}
      <div
        className={cx('authDisclaimer', expandedDisclaimer && 'expanded')}
        data-testid='authDisclaimer'
      >
        {!expandedDisclaimer ? (
          <>
            <>{t('checkout-auth.auth-disclaimer')}</>
            <button
              onClick={() => setExpandedDisclaimer(!expandedDisclaimer)}
              type='button'
            >
              {t('checkout-auth.learn-more')}
            </button>
          </>
        ) : (
          <Trans
            i18nKey='checkout-auth.auth-disclaimer-expanded'
            components={{
              ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
              CAInfo: <CAInfo />
            }}
          />
        )}
      </div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.GuestCheckoutAutomatic}
          accountInfo={{
            phoneNumber,
            firstName: getValues('yourInfoFirstName'),
            lastName: getValues('yourInfoLastName'),
            email: getValues('yourInfoEmail')
          }}
          currentLoginStage={loginStage}
          showBackButton={false}
          saveValuesForCheckout={(formInputs) =>
            saveValuesForCheckout(formInputs, setValue)
          }
          onCloseAction={() => setAutoPromptAuth(false)}
        />
      </Modal>
    </CheckoutSection>
  )
}

export const OrDivider = () => {
  return (
    <div className='checkoutCustomerDivider'>
      <hr />
      <span>{t('checkout-auth.or')}</span>
      <hr />
    </div>
  )
}

export default CheckoutAuth
